/**
 * Friendly timezone labels for General Settings.
 * Stored value remains the canonical IANA id (e.g. Asia/Jerusalem).
 */

export type TimezoneOption = {
  id: string;
  label: string;
  searchText: string;
};

const REGION_DISPLAY_NAMES =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(undefined, { type: "region" })
    : null;

function padOffset(totalMinutes: number): string {
  const sign = totalMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(totalMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/** Current UTC offset for an IANA zone, accounting for DST at `at`. */
export function timezoneUtcOffsetLabel(timeZone: string, at: Date = new Date()): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    });
    const part = formatter.formatToParts(at).find((item) => item.type === "timeZoneName")?.value;
    if (part) {
      const normalized = part.replace("GMT", "UTC").replace("UTC", "UTC");
      if (/^UTC[+-]\d/.test(normalized) || normalized === "UTC") {
        if (normalized === "UTC") return "UTC+00:00";
        const match = normalized.match(/^UTC([+-])(\d{1,2})(?::?(\d{2}))?$/);
        if (match) {
          const hours = Number(match[2]);
          const minutes = match[3] ? Number(match[3]) : 0;
          return `UTC${match[1]}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
        }
      }
    }
  } catch {
    // Fall through to offset math.
  }

  try {
    const utc = new Date(at.toLocaleString("en-US", { timeZone: "UTC" }));
    const local = new Date(at.toLocaleString("en-US", { timeZone }));
    const diffMinutes = Math.round((local.getTime() - utc.getTime()) / 60_000);
    return `UTC${padOffset(diffMinutes)}`;
  } catch {
    return "UTC";
  }
}

function cityFromId(timeZone: string): string {
  const segment = timeZone.split("/").pop() ?? timeZone;
  return segment.replace(/_/g, " ");
}

function regionHint(timeZone: string): string | null {
  const parts = timeZone.split("/");
  if (parts.length < 2) return null;
  const regionCodeGuess: Record<string, string> = {
    Jerusalem: "IL",
    Tel_Aviv: "IL",
    London: "GB",
    Paris: "FR",
    Berlin: "DE",
    New_York: "US",
    Los_Angeles: "US",
    Chicago: "US",
    Denver: "US",
    Toronto: "CA",
    Sao_Paulo: "BR",
    Dubai: "AE",
    Tokyo: "JP",
    Singapore: "SG",
    Sydney: "AU",
    Auckland: "NZ",
    Cairo: "EG",
    Kolkata: "IN",
    Mumbai: "IN",
  };
  const cityKey = parts[parts.length - 1] ?? "";
  const code = regionCodeGuess[cityKey];
  if (!code || !REGION_DISPLAY_NAMES) return null;
  try {
    return REGION_DISPLAY_NAMES.of(code) ?? null;
  } catch {
    return null;
  }
}

/** Customer-facing label; value stored/sent remains `timeZone` IANA id. */
export function formatTimezoneLabel(timeZone: string, at: Date = new Date()): string {
  if (!timeZone) return "—";
  const offset = timezoneUtcOffsetLabel(timeZone, at);
  const city = cityFromId(timeZone);
  const region = regionHint(timeZone);
  if (region && region.toLowerCase() !== city.toLowerCase()) {
    return `(${offset}) ${city} / ${region}`;
  }
  return `(${offset}) ${city}`;
}

export function buildTimezoneOptions(ids: readonly string[], at: Date = new Date()): TimezoneOption[] {
  return ids.map((id) => {
    const label = formatTimezoneLabel(id, at);
    const region = regionHint(id) ?? "";
    const searchText = [id, label, cityFromId(id), region, id.replace(/_/g, " ")]
      .join(" ")
      .toLowerCase();
    return { id, label, searchText };
  });
}

export function filterTimezoneOptions(
  options: readonly TimezoneOption[],
  query: string,
): TimezoneOption[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [...options];
  return options.filter((option) => option.searchText.includes(needle));
}
